import { Box, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import type { ArticleBody, BlockNode, PublicAd } from '@ahora-patagonia/contracts';
import { EspacioPublicitario } from '../../../../shared/ads/EspacioPublicitario.js';
import { entrenotas } from '../../../../shared/ads/cadencia.js';
import { SIZES, srcsetDe } from '../../../../shared/media/srcset.js';
import { EmbedDeRed } from './EmbedDeRed.js';
import { InlineTexto, type InlineNode } from './InlineTexto.js';
import { VideoDiferido } from './VideoDiferido.js';

export interface RendererCuerpoProps {
  body: ArticleBody;
  /** El entrenota (`IN_ARTICLE`), intercalado por `entrenotas()`. Sin campaña, nada. */
  adEntrenota: PublicAd | undefined;
}

/**
 * El renderer del cuerpo enriquecido (handoff §10.1): mapea los **siete tipos de nodo** del
 * `body` Json — párrafo, subtítulo (H2/H3), lista con viñetas, cita destacada, imagen, video y
 * publicación de una red — y **descarta en silencio cualquier otro**. Jamás
 * `dangerouslySetInnerHTML`: el cuerpo es un árbol validado por contrato, no una cadena de marcado.
 *
 * También intercala los espacios `IN_ARTICLE`: el primero tras el 2º párrafo (solo si hay
 * al menos 5), el segundo al final del cuerpo.
 */
export function RendererCuerpo({ body, adEntrenota }: RendererCuerpoProps) {
  const nParrafos = body.content.filter((n) => n.type === 'paragraph').length;
  const cadencia = entrenotas(nParrafos);

  let parrafosVistos = 0;
  return (
    <Box>
      {body.content.map((nodo, i) => {
        const dibujado = renderBloque(nodo, i);
        if (nodo.type === 'paragraph') parrafosVistos += 1;
        const conAd = adEntrenota && nodo.type === 'paragraph' && parrafosVistos === cadencia.trasParrafo;
        return (
          <Fragment key={i}>
            {dibujado}
            {conAd && (
              <Box sx={{ my: 3 }}>
                <EspacioPublicitario ubicacion="IN_ARTICLE" ad={adEntrenota} />
              </Box>
            )}
          </Fragment>
        );
      })}
      {adEntrenota && cadencia.alFinal && (
        <Box sx={{ mt: 3 }}>
          <EspacioPublicitario ubicacion="IN_ARTICLE" ad={adEntrenota} />
        </Box>
      )}
    </Box>
  );
}

function renderBloque(nodo: BlockNode, key: number): ReactNode {
  switch (nodo.type) {
    case 'paragraph':
      return (
        <Typography key={key} variant="cuerpoNota" component="p" sx={{ my: 2 }}>
          <InlineTexto nodos={nodo.content as InlineNode[] | undefined} />
        </Typography>
      );

    case 'heading':
      // Lo único que corta un texto largo, así que se dibuja fuerte: regla de 2 px arriba
      // y 32 px de aire. Se lee como el comienzo de un tramo, no como negrita.
      return (
        <Typography
          key={key}
          variant="subtituloCuerpo"
          component={nodo.attrs.level === 2 ? 'h2' : 'h3'}
          sx={{ borderTop: `2px solid ${palette.ink}`, pt: 1.5, mt: 4, mb: 2 }}
        >
          <InlineTexto nodos={nodo.content as InlineNode[] | undefined} />
        </Typography>
      );

    case 'bulletList':
      return (
        <Box key={key} component="ul" sx={{ my: 2, pl: 3 }}>
          {nodo.content.map((item, j) => (
            <Typography key={j} variant="cuerpoNota" component="li" sx={{ my: 0.5 }}>
              {item.content.map((hijo, k) => (
                <Fragment key={k}>{renderBloqueDeItem(hijo, k)}</Fragment>
              ))}
            </Typography>
          ))}
        </Box>
      );

    case 'blockquote': {
      // El único recurso de énfasis: barra roja de 4 px, sin fondo ni comillas decorativas.
      // Si la cita trae más de un bloque, el último se lee como atribución (13 px).
      const bloques = nodo.content;
      return (
        <Box
          key={key}
          component="blockquote"
          sx={{ borderLeft: `4px solid ${palette.systemRed}`, m: 0, my: 3, pl: 2.5 }}
        >
          {bloques.map((hijo, j) => {
            const esAtribucion = bloques.length > 1 && j === bloques.length - 1;
            if (hijo.type === 'paragraph') {
              return (
                <Typography
                  key={j}
                  variant={esAtribucion ? 'firma' : 'citaDestacada'}
                  component="p"
                  sx={esAtribucion ? { color: neutral[700], mt: 1 } : {}}
                >
                  <InlineTexto nodos={hijo.content as InlineNode[] | undefined} />
                </Typography>
              );
            }
            return <Fragment key={j}>{renderBloque(hijo, j)}</Fragment>;
          })}
        </Box>
      );
    }

    case 'image': {
      // Al ancho de la columna, nunca fuera del margen. La vertical va en su proporción
      // original limitada a 80 vh: **nunca se recorta a panorámica dentro del cuerpo**.
      //
      // Por eso el `srcset` sale de `full` y no de un recorte: `full` es la imagen entera, solo
      // redimensionada, así que baja liviana sin cambiarle el encuadre. Sin variantes —una foto
      // subida antes del worker— cae a la original, que es lo que hacía siempre.
      //
      // La dirección la resolvió la API a partir del `mediaId`: el cuerpo guarda la referencia a
      // la foto, no su URL.
      const fuente = srcsetDe(nodo.attrs.imagen ?? null, 'full');
      if (!fuente) return null;
      const medida = nodo.attrs.imagen?.variants?.full?.at(-1) ?? nodo.attrs.imagen;
      return (
        <Box key={key} component="figure" sx={{ m: 0, my: 3 }}>
          <img
            src={fuente.src}
            {...(fuente.srcSet ? { srcSet: fuente.srcSet, sizes: SIZES.cuerpo } : {})}
            alt={nodo.attrs.alt}
            loading="lazy"
            // Con las dos medidas el browser reserva la caja antes de bajar la foto: sin esto el
            // texto salta cuando entra cada imagen del cuerpo.
            {...(medida?.width && medida.height
              ? { width: medida.width, height: medida.height }
              : {})}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              height: 'auto',
              display: 'block',
              margin: '0 auto',
            }}
          />
          {nodo.attrs.caption && (
            <Typography
              component="figcaption"
              variant="epigrafe"
              sx={{ color: neutral[700], mt: 0.5 }}
            >
              {nodo.attrs.caption}
            </Typography>
          )}
        </Box>
      );
    }

    case 'video':
      return <VideoDiferido key={key} videoId={nodo.attrs.videoId} caption={nodo.attrs.caption} />;

    case 'embed':
      return (
        <EmbedDeRed
          key={key}
          provider={nodo.attrs.provider}
          postId={nodo.attrs.postId}
          url={nodo.attrs.url}
          caption={nodo.attrs.caption}
        />
      );

    default:
      // Lista blanca cerrada: un tipo desconocido no se dibuja y no rompe nada.
      return null;
  }
}

/** Dentro de un `listItem` un párrafo va sin márgenes propios; el resto, igual que arriba. */
function renderBloqueDeItem(nodo: BlockNode, key: number): ReactNode {
  if (nodo.type === 'paragraph') {
    return <InlineTexto nodos={nodo.content as InlineNode[] | undefined} />;
  }
  return renderBloque(nodo, key);
}
